/**
 * MailingAddressSheet — the optional family mailing address (train 17).
 *
 * Copy from the operator, verbatim. Skip carries the same weight as Save (same
 * size, its own colour), because giving an address is never the expected path.
 * Used once after a purchase (MailingAddressPrompt) and from Settings → Plan &
 * billing (MailingAddressRow). The fields carry autocomplete tokens so the
 * iPad's AutoFill can offer the grown-up's contact card. Save and Skip stay in
 * view with the fields scrolling above them, keyboard up or not.
 */
import { useState } from "react";
import { PanelSurface } from "../chrome/PanelSurface";
import { ChromeTokens } from "../chrome/tokens";
import { useWebKeyboardHeight } from "../chrome/webKeyboardHeight";
import { billingPlatform } from "./platform";
import type { MailingAddress } from "./mailingAddress";

export const MAILING_ADDRESS_COPY =
  "Where can we send your family a little something now and then? Optional. We never promise to send anything, we share it only with the carrier, and you can remove it any time.";

export interface MailingAddressSheetProps {
  open: boolean;
  initial?: MailingAddress | null;
  /** Rejects to keep the sheet open with a message. */
  onSave: (address: MailingAddress) => Promise<void>;
  /** Skip, the backdrop and Escape all land here. */
  onSkip: () => void;
  skipLabel?: string;
}

type Field = keyof MailingAddress;

type Capitalize = "words" | "characters" | "sentences" | "off";

// iOS typing: words for names, letters for the country, and no autocorrect on
// street names or postcodes, which it mangles.
const FIELDS: {
  key: Field; label: string; autoComplete: string; required: boolean; maxLength: number;
  autoCapitalize: Capitalize; autoCorrect: boolean;
}[] = [
  { key: "name", label: "Name", autoComplete: "name", required: true, maxLength: 120, autoCapitalize: "words", autoCorrect: false },
  { key: "line1", label: "Address line 1", autoComplete: "address-line1", required: true, maxLength: 200, autoCapitalize: "words", autoCorrect: false },
  { key: "line2", label: "Address line 2 (optional)", autoComplete: "address-line2", required: false, maxLength: 200, autoCapitalize: "words", autoCorrect: false },
  { key: "city", label: "City", autoComplete: "address-level2", required: true, maxLength: 100, autoCapitalize: "words", autoCorrect: false },
  { key: "region", label: "State / region (optional)", autoComplete: "address-level1", required: false, maxLength: 100, autoCapitalize: "words", autoCorrect: false },
  { key: "postal_code", label: "ZIP / postal code", autoComplete: "postal-code", required: true, maxLength: 20, autoCapitalize: "characters", autoCorrect: false },
  { key: "country", label: "Country (2 letters)", autoComplete: "country", required: true, maxLength: 2, autoCapitalize: "characters", autoCorrect: false },
];

const MISSING_ID = "mailing-address-missing";

function blank(initial?: MailingAddress | null): Record<Field, string> {
  return {
    name: initial?.name ?? "",
    line1: initial?.line1 ?? "",
    line2: initial?.line2 ?? "",
    city: initial?.city ?? "",
    region: initial?.region ?? "",
    postal_code: initial?.postal_code ?? "",
    country: initial?.country ?? "US",
  };
}

export function MailingAddressSheet({ open, initial, onSave, onSkip, skipLabel = "Skip" }: MailingAddressSheetProps): JSX.Element | null {
  useWebKeyboardHeight(open && billingPlatform().manages);
  const [values, setValues] = useState(() => blank(initial));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  if (!open) return null;

  const complete =
    FIELDS.every((f) => !f.required || values[f.key].trim() !== "") && /^[A-Za-z]{2}$/.test(values.country.trim());

  const save = async () => {
    if (!complete || saving) return;
    setSaving(true);
    setError("");
    try {
      await onSave({
        name: values.name.trim(),
        line1: values.line1.trim(),
        line2: values.line2.trim() || null,
        city: values.city.trim(),
        region: values.region.trim() || null,
        postal_code: values.postal_code.trim(),
        country: values.country.trim().toUpperCase(),
      });
    } catch {
      setError("We couldn't save that just now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const pill = {
    flex: 1,
    minHeight: 48,
    padding: "10px 22px",
    border: "none",
    borderRadius: ChromeTokens.radius.pill,
    fontFamily: ChromeTokens.font.ui,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    color: ChromeTokens.color.ink,
  } as const;

  return (
    <PanelSurface open anchor="center" onDismiss={onSkip} ariaLabel="Mailing address">
      <form
        data-testid="mailing-address-sheet"
        onSubmit={(e) => { e.preventDefault(); void save(); }}
        style={{
          width: "min(420px, calc(100vw - 80px))",
          fontFamily: ChromeTokens.font.ui,
          color: ChromeTokens.color.ink,
          maxHeight: `calc(100dvh - var(--keyboard-h, 0px) - ${32 + 2 * ChromeTokens.spacing.xl}px)`,
          display: "flex",
          flexDirection: "column",
          gap: ChromeTokens.spacing.sm,
        }}
      >
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm }}>
        <p style={{ fontSize: 15, lineHeight: 1.5, margin: 0 }}>{MAILING_ADDRESS_COPY}</p>
        {FIELDS.map((f, i) => (
          <label key={f.key} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13, fontWeight: 600 }}>
            {f.label}
            <input
              name={f.key}
              autoComplete={f.autoComplete}
              autoCapitalize={f.autoCapitalize}
              autoCorrect={f.autoCorrect ? "on" : "off"}
              spellCheck={f.autoCorrect}
              enterKeyHint={i < FIELDS.length - 1 ? "next" : "done"}
              aria-required={f.required || undefined}
              maxLength={f.maxLength}
              value={values[f.key]}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              // Keep the field in view inside the card as the keyboard rises.
              onFocus={(e) => e.currentTarget.scrollIntoView?.({ block: "nearest" })}
              style={{
                minHeight: 44,
                padding: "8px 12px",
                borderRadius: ChromeTokens.radius.md,
                border: `1px solid ${ChromeTokens.color.baseMuted}`,
                background: ChromeTokens.color.base,
                color: ChromeTokens.color.ink,
                fontFamily: ChromeTokens.font.ui,
                fontSize: 16,
              }}
            />
          </label>
        ))}
        </div>
        {error && (
          <div role="alert" style={{ fontSize: 13, color: ChromeTokens.color.danger }}>{error}</div>
        )}
        <div style={{ display: "flex", gap: ChromeTokens.spacing.sm, marginTop: ChromeTokens.spacing.sm }}>
          <button type="button" onClick={onSkip} style={{ ...pill, background: ChromeTokens.color.baseMuted }}>
            {skipLabel}
          </button>
          <button
            type="submit"
            disabled={!complete || saving}
            aria-describedby={complete ? undefined : MISSING_ID}
            style={{ ...pill, background: ChromeTokens.color.sun, opacity: complete ? 1 : 0.6 }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        {!complete && (
          <div id={MISSING_ID} style={{ fontSize: 12, color: ChromeTokens.color.inkMuted, textAlign: "center" }}>
            Save needs a name, the first address line, a city, a ZIP or postal code and a 2-letter country.
          </div>
        )}
      </form>
    </PanelSurface>
  );
}
